import {
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
  viewChildren,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { Observable, ObservedValueOf, firstValueFrom } from "rxjs";
import { combineLatestWith, map } from "rxjs/operators";
import { RouteKey as ArticlesRouteKey, routes as articleRoutes } from "src/app/pages/articles-page/routes";
import { RouteKey as ContractsRouteKey, routes as contractsRoutes } from "src/app/pages/contracts-page/routes";
import { RouteKey as PlanRouteKey, routes as pensionPlanRoutes } from "src/app/modules/pension-plan/routes";
import {
  RouteKey as SmartAccountRouteKey,
  routes as smartAccountRoutes,
} from "src/app/pages/smart-account-page/routes";
import { RouteKey, routes } from "src/app/routes";
import { CommonParametersService } from "src/app/services/common-parameters.service";
import { ConsentService } from "src/app/services/consent.service";
import { CopyValueToClipboardService } from "src/app/services/copy-value-to-clipboard.service";
import { ClientDataService } from "src/app/services/customer-supplied-data/client-data.service";
import { ProfileService } from "src/app/services/customer-supplied-data/profile.service";
import { CustomerService, getNin } from "src/app/services/customer.service";
import { DispatcherService } from "src/app/services/dispatcher.service";
import { ErrorsService } from "src/app/services/errors.service";
import { ExtrapensionService, ExtrapensionState } from "src/app/services/extrapension.service";
import { FeatureToggleService, OverridableFeatureTogglesMap } from "src/app/services/feature-toggle.service";
import { HotjarService } from "src/app/services/hotjar.service";
import { IncomeService } from "src/app/services/income/income.service";
import { KeycloakService } from "src/app/services/keycloak.service";
import { NavigateToPensionPlanService } from "src/app/services/navigate-to-pension-plan.service";
import { PensionPlanProgressService } from "src/app/services/pension-plan-progress.service";
import { PensionPlanService } from "src/app/services/pension-plan.service";
import { PublicPensionService } from "src/app/services/prognoses-services/public-pension.service";
import { RemoteConfigService } from "src/app/services/remote-config.service";
import { LoadingStateService } from "src/app/services/loading-state/loading-state.service";
import { GlobalRunningJobsService } from "src/app/services/running-jobs/global-running-jobs.service";
import { SessionStorageService } from "src/app/services/session-storage.service";
import { SmartAccountService, SmartAccountState } from "src/app/services/smart-account.service";
import { StartPayoutAgeService } from "src/app/services/start-payout-age.service";
import { Log } from "src/app/utils/log";
import { Monitoring } from "src/app/utils/monitoring";
import { getEnumKeys } from "src/app/utils/utils";
import {
  MOCK_SCENARIO_PRESETS,
  PRESET_IDS,
  PRESET_LABELS,
  PresetId,
  getPreset,
  readMockScenario,
  writeMockScenario,
} from "src/app/v2/mocks/mock-scenario";
import { environment } from "src/environments/environment";
import { clearAiMockOverride } from "../ai-mock-chat/ai-mock-chat-storage";
import { isOfflineOverrideAllowed, setOfflineModeOverride } from "src/environments/offline-mode-override";

import { AsyncPipe, JsonPipe, KeyValuePipe } from "@angular/common";
import { ModalModule } from "@storeblocks/modal-ng";
import { BadgeModule } from "@storeblocks/badge-ng";
import { CheckboxModule } from "@storeblocks/checkbox-ng";
import { AlertModule } from "@storeblocks/alert-ng";
import { select$ } from "src/app/utils/rxjs/select";
import { ButtonModule } from "@storeblocks/button-ng";
import { FormModule } from "@storeblocks/form-ng";
import { IconModule } from "@storeblocks/icon-ng";
import { SwitchModule } from "@storeblocks/switch-ng";
import { LetDirective } from "src/app/modules/shared/directives/let.directive";
import { FmsPipe } from "src/app/modules/shared/pipes/fms.pipe";
import {
  ActionIpsService,
  IpsAction,
} from "src/app/modules/features/pension-actions/components/action-ips/action-ips.service";
import { Cookies, CookieService } from "src/app/services/cookie.service";
import { DialogRef } from "src/app/services/dialog/dialog-ref";
import { DevPanelContext } from "./dev-panel-context";
import { DevPanelFilter } from "./dev-panel-filter";
import { DevRowDirective } from "./dev-row.directive";
import { DevRowComponent } from "./dev-row.component";
import { DevSectionDirective } from "./dev-section.directive";
import { DevFetchActionsService } from "../dev-fetch-actions.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DevFeatureEnvController } from "../dev-feature-env.controller";
import { DEV_ROW_TYPE_ICONS } from "./dev-row-type";
import { PhosphorIconName } from "@storeblocks/icon-ng";
import { DevPanelShell, type DevSectionId } from "./dev-panel-shell";
import { type DevAvailabilityRuleKey } from "./dev-availability";

type InspectorId =
  | "env"
  | "remoteConfig"
  | "customer"
  | "consents"
  | "simParams"
  | "profile"
  | "clientData"
  | "keycloakToken"
  | "sessionStorage"
  | "lifeExpectancy"
  | "publicPensionData";

interface Inspector {
  id: InspectorId;
  label: string;
  /** Extra search aliases for the dev panel filter. */
  keywords: readonly string[];
  /** Optional availability rule for this inspector. */
  availability?: DevAvailabilityRuleKey;
}

@Component({
  selector: "app-dev-panel-modal",
  templateUrl: "./dev-panel-modal.component.html",
  standalone: true,
  imports: [
    ModalModule,
    LetDirective,
    CheckboxModule,
    AsyncPipe,
    JsonPipe,
    KeyValuePipe,
    FmsPipe,
    AlertModule,
    ButtonModule,
    FormModule,
    BadgeModule,
    IconModule,
    SwitchModule,
    DevRowDirective,
    DevRowComponent,
    DevSectionDirective,
  ],
  providers: [DevPanelFilter, DevPanelContext, DevPanelShell],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // The modal is appended to document.body, outside the <app-dev> subtree
    // that carries data-phosphor-icons="true" in app.component.html —
    // without this the phosphor <i> glyphs in the panel render as garbled
    // boxes.
    "data-phosphor-icons": "true",
  },
  styles: [
    `
      /**
       * stb-switch and stb-form render {{ label }} unconditionally with no
       * aria-only input. The visible label is supplied by dev-row, so the
       * control's own label is clipped to avoid a visible duplicate while
       * preserving its accessible name — dev-row's label is a plain <div>, so
       * dropping the real label would leave the control unnamed. Uses the
       * sr-only/clip technique (not display:none or visibility:hidden, which
       * risk removing the label from the a11y tree).
       * Depends on design-system internal markup (stb-switch → div.switch-container → label).
       * !important on position/padding wins over DS .right-aligned label styles (specificity 0,3,1).
       */
      :host ::ng-deep .dev-switch-clipped-label label,
      :host ::ng-deep .dev-form-clipped-label label {
        position: absolute !important;
        width: 1px;
        height: 1px;
        padding: 0 !important;
        margin: -1px;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
        border: 0;
      }

      /**
       * S0: Neutralize the DS modal scroller so only the content pane scrolls.
       * The rail must stay put while the pane moves. Anchored at :host to avoid
       * breaking every other modal in the app.
       *
       * display: flex is load-bearing: .content has max-height (not height), so
       * its used height is auto (merely capped). Percentage heights inside it
       * cannot resolve. Making it a flex column container lets its children be
       * bounded by the max-height, allowing the overflow chain to engage.
       */
      :host ::ng-deep .stb-modal .internal .content {
        /**
         * !important because the design system's own rule for this element is
         * emulated-encapsulation-scoped — .stb-modal[_ngcontent-x]
         * .internal[_ngcontent-x] .content[_ngcontent-x], specificity (0,6,0) —
         * and declares overflow: auto. Ours is (0,4,0) and silently lost, so
         * the modal scrolled as well as the pane: two scrollbars side by side.
         */
        overflow: hidden !important;
        display: flex;
        flex-direction: column;
      }
    `,
  ],
})
export class DevPanelModalComponent {
  // 1. Private instance fields (DI and inject() calls first)
  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly pensionPlanProgressService = inject(PensionPlanProgressService);
  private readonly pensionPlanService = inject(PensionPlanService);
  private readonly remoteConfigService = inject(RemoteConfigService);
  private readonly copyValueToClipboardService = inject(CopyValueToClipboardService);
  private readonly navigateToPensionPlanService = inject(NavigateToPensionPlanService);
  private readonly cookieService = inject(CookieService);
  private readonly dialogRef = inject(DialogRef);

  // 2. Public decorated fields (Inputs/Outputs, ViewChild, HostBinding, etc.)
  readonly context = inject(DevPanelContext);
  readonly filterInput = viewChild<ElementRef<HTMLInputElement>>("filterInput");
  readonly rows = viewChildren(DevRowDirective);
  readonly sections = viewChildren(DevSectionDirective);

  // 3. Public instance fields
  readonly router = inject(Router);
  readonly filter = inject(DevPanelFilter);
  readonly shell = inject(DevPanelShell);
  readonly devFetchActions = inject(DevFetchActionsService);
  clientDataService = inject(ClientDataService);
  commonParametersService = inject(CommonParametersService);
  consentService = inject(ConsentService);
  customerService = inject(CustomerService);
  dispatcherService = inject(DispatcherService);
  errorsService = inject(ErrorsService);
  globalRunningJobsService = inject(GlobalRunningJobsService);
  incomeService = inject(IncomeService);
  keycloakService = inject(KeycloakService);
  profileService = inject(ProfileService);
  publicPensionService = inject(PublicPensionService);
  loadingStateService = inject(LoadingStateService);
  smartAccountService = inject(SmartAccountService);
  readonly sessionStorageService = inject(SessionStorageService);
  readonly hotjarService = inject(HotjarService);
  readonly extrapensionService = inject(ExtrapensionService);
  readonly startPayoutAgeService = inject(StartPayoutAgeService);
  readonly actionIpsService = inject(ActionIpsService);
  env = environment;
  remoteConfig$ = this.remoteConfigService.getAllConfigs$();
  customer$ = this.customerService.customer$;
  nin$ = this.customerService.customer$.pipe(map((customer) => getNin(customer)));
  cmid$ = this.keycloakService.cmid.user$;
  profile$ = this.profileService.profile$;
  clientData$: Observable<{
    clientDataWrapper: CustomerSuppliedData.ClientDataWrapper;
    simulationParametersByEngagementEnable: ObservedValueOf<
      typeof ClientDataService.prototype.simulationParametersByEngagementEnable$
    >;
    simulationParametersPartialWithdrawalByEngagementEnable: ObservedValueOf<
      typeof ClientDataService.prototype.simulationParametersPartialWithdrawalEnable$
    >;
  }>;
  public offlineMode = environment.offlineMode;
  /** Whether the current context permits toggling offline mode (dev or non-prod stage). */
  public readonly offlineModeToggleAllowed = isOfflineOverrideAllowed(environment.production);

  public readonly presetIds = PRESET_IDS;
  public readonly presetLabels = PRESET_LABELS;
  public readonly selectedPresetId = signal<PresetId>(readMockScenario().preset);

  public readonly SmartAccountState = SmartAccountState;
  public readonly ExtrapensionState = ExtrapensionState;
  public readonly IpsAction = IpsAction;

  lifeExpectancyData$: Observable<unknown> = this.customerService.age$.pipe(
    combineLatestWith(this.customerService.dateOfBirth$, this.customerService.ageInMonths$),
    map(([age, dateOfBirth, ageInMonths]) => ({ customerAge: age, ageInMonths, dateOfBirth })),
  );
  publicPensionData$: typeof PublicPensionService.prototype.hotStreamsOfSimulationParametersByEngagement$ =
    this.publicPensionService.hotStreamsOfSimulationParametersByEngagement$;

  public readonly featureToggles$: Observable<OverridableFeatureTogglesMap>;
  public readonly afpValue$ = this.profileService.hasAfp$.pipe(map((hasAfp) => hasAfp.toString()));
  public readonly startPayoutAge$ = this.startPayoutAgeService.getStartPayoutAge();
  public readonly startPayoutAgePartialWithdrawal$: Observable<number>;
  public readonly startPayoutAgeRange$ = this.startPayoutAgeService.getStartPayoutAgeRange();
  public readonly partTimePercentage$: Observable<number>;
  public readonly withdrawalPercentage$: Observable<number>;

  public pensionPlanRoutes$ = this.pensionPlanProgressService.planRoutes$;
  public prognosesSpinner$: Observable<string[]> = toObservable(this.loadingStateService.runningJobIds);
  public readonly pages = getPages();
  public readonly ipsStateValue$ = this.actionIpsService.getIpsAction();

  // The 11 JSON inspectors shown in the Diagnostics section. Each is lazily
  // subscribed: its body only renders (and only then does the async pipe
  // subscribe) when the Diagnostics tab becomes active.
  public readonly inspectors: readonly Inspector[] = [
    { id: "env", label: "Env", keywords: ["environment", "config", "diagnostics", "inspect", "runtime"] },
    {
      id: "remoteConfig",
      label: "RemoteConfig",
      keywords: ["remote config", "firebase", "diagnostics", "inspect", "runtime"],
    },
    { id: "customer", label: "Customer", keywords: ["customer data", "diagnostics", "inspect", "runtime"] },
    { id: "consents", label: "Consents", keywords: ["consent", "gdpr", "diagnostics", "inspect", "runtime"] },
    {
      id: "simParams",
      label: "Sim-params",
      keywords: ["simulation", "parameters", "diagnostics", "inspect", "runtime"],
    },
    {
      id: "profile",
      label: "Profile",
      keywords: ["customer supplied", "profile", "diagnostics", "inspect", "runtime"],
    },
    {
      id: "clientData",
      label: "ClientData",
      keywords: ["client data", "simulation parameters", "diagnostics", "inspect", "runtime"],
    },
    {
      id: "keycloakToken",
      label: "KC Tok",
      keywords: ["keycloak", "token", "jwt", "id token", "diagnostics", "inspect", "runtime"],
      availability: "requiresOnline",
    },
    {
      id: "sessionStorage",
      label: "SessionStorage",
      keywords: ["session storage", "storage", "diagnostics", "inspect", "runtime"],
    },
    {
      id: "lifeExpectancy",
      label: "LifeExpectancy",
      keywords: ["life expectancy", "age", "diagnostics", "inspect", "runtime"],
    },
    {
      id: "publicPensionData",
      label: "PublicPensionData",
      keywords: ["public pension", "folketrygden", "diagnostics", "inspect", "runtime"],
    },
  ];

  /** Whether the feature-toggles list is expanded independently of filtering. */
  public readonly featureTogglesOpen = signal(false);

  public readonly totalRowCount = computed(() => this.rows().length);
  public readonly visibleRowCount = computed(() => this.rows().filter((row) => row.reachable()).length);
  /**
   * Route-pinned rows matching the query. Nothing renders this since the "This
   * page" chip block was replaced by quicklinks; kept deliberately, because
   * `pinned()` still decides what sinks and this is the only handle on it.
   */
  public readonly pinnedRows = computed(() => this.rows().filter((row) => row.pinned() && row.matches()));
  /**
   * The `pages` map as quicklinks for the "This page" tab, each flagged when it
   * is the page currently open so its button can be disabled.
   */
  public readonly pageLinks = computed<readonly PageLink[]>(() =>
    Array.from(this.pages, ([label, path]) => ({
      label,
      path,
      current: this.context.matchesRoute([lastRouteSegment(path)]),
    })),
  );
  public readonly currentPageLabel = computed(() => {
    const segments = this.context.routeSegments();
    return segments[segments.length - 1] ?? "dashboard";
  });

  public readonly railItems = computed<readonly RailItem[]>(() => {
    const byId = new Map(
      this.sections()
        .filter((s) => s.id())
        .map((s) => [s.id()!, s]),
    );
    return RAIL_DEFS.map((def) => ({
      ...def,
      matchCount: byId.get(def.id)?.matchingRowCount() ?? 0,
      showCount: def.id !== "page",
    }));
  });

  // OnPush hazard: this used to be a bare `consentService.extractAllConsents()`
  // call in the template, which stayed fresh only because of the old default-CD
  // 1Hz tick. Recomputed on inspector selection and via the detail-pane refresh
  // button to preserve the live behaviour under OnPush.
  public readonly extractedConsents = signal(this.consentService.extractAllConsents());

  // OnPush hazard: same story as extractedConsents, for the direct
  // sessionStorage read that used to be a bare `sessionStorageService.getAppData()`
  // call in the template.
  public readonly sessionStorageSnapshot = signal(this.sessionStorageService.getAppData());

  // These used to be methods re-invoked on every CD cycle even though they
  // return static enum lists; computed once at construction instead.
  public readonly smartAccountStateKeysList: ReadonlyArray<string> = getEnumKeys(this.SmartAccountState);
  public readonly extrapensionStateKeysList: ReadonlyArray<string> = getEnumKeys(this.ExtrapensionState);
  public readonly ipsStateValuesList: ReadonlyArray<string> = Object.values(this.IpsAction);

  // 3. Public mutable properties
  dispatchLogging = this.dispatcherService.isLogging;

  // FormControls for switches
  public readonly advisorModeControl = new FormControl(false);
  public readonly dispatchLoggingControl = new FormControl(this.dispatcherService.isLogging);
  public readonly offlineModeControl = new FormControl(this.offlineMode ?? false);

  // 4. Protected instance fields
  protected readonly featureEnv = inject(DevFeatureEnvController);

  constructor() {
    // Feed the context with the current route and environment state. The panel
    // is a modal, so we capture the state at open time rather than tracking
    // live navigation — the user opened the panel in a specific context, and
    // that context should remain stable while the panel is visible.
    this.context.setUrl(this.router.url);
    this.context.setOfflineMode(this.offlineMode ?? false);

    this.keycloakService.forcedAdvisorMode$.pipe(takeUntilDestroyed()).subscribe((advisorMode) => {
      this.context.setAdvisorMode(advisorMode ?? false);
      this.advisorModeControl.setValue(advisorMode ?? false, { emitEvent: false });
    });

    // Wire switch controls to their toggle methods
    this.advisorModeControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.toggleAdvisorMode());
    this.dispatchLoggingControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      // toggleDispatcherLogging() already updates this.dispatchLogging from the service
      this.toggleDispatcherLogging();
      this.dispatchLoggingControl.setValue(this.dispatchLogging, { emitEvent: false });
    });
    this.offlineModeControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (value !== null) {
        this.setOfflineMode(value);
      }
    });

    this.clientData$ = this.clientDataService.clientDataWrapper$.pipe(
      combineLatestWith(
        this.clientDataService.simulationParametersByEngagementEnable$,
        this.clientDataService.simulationParametersPartialWithdrawalEnable$,
      ),
      map(([clientDataWrapper, enable, partialEnable]) => ({
        clientDataWrapper,
        simulationParametersByEngagementEnable: enable,
        simulationParametersPartialWithdrawalByEngagementEnable: partialEnable,
      })),
    );

    this.featureToggles$ = this.featureToggleService.getOverridableFeatureToggleMap();

    this.startPayoutAgePartialWithdrawal$ = select$(this.clientDataService.clientDataWrapper$, (data) =>
      Number(data.clientData?.simulationParameters.startPayoutAgePartialWithdrawal),
    );

    this.partTimePercentage$ = select$(this.clientDataService.clientDataWrapper$, (data) =>
      Number(data.clientData?.simulationParameters.partTimePercentage),
    );

    this.withdrawalPercentage$ = select$(this.clientDataService.clientDataWrapper$, (data) =>
      Number(data.clientData?.simulationParameters.withdrawalPercentage),
    );

    // Autofocus the filter input. `DialogConfig.autoFocus` is dead code in
    // DialogService, and stb-modal's ModalComponent only auto-focuses its own
    // close button on an explicit false→true `isShowing` transition (this
    // component always renders with `[isShowing]="true"`), so neither of
    // those mechanisms will do it for us.
    afterNextRender(() => {
      this.filterInput()?.nativeElement.focus();
    });

    // The main reason to have a filter is "I know the control exists, I just
    // can't remember which tab" — so a query that empties the active section
    // moves the user to the first section that still has matches. Only while a
    // query is active: clearing the filter leaves the user where they are.
    //
    // The active section is read untracked, so this responds to the query
    // changing and not to the user picking a tab. Tracking it meant selecting an
    // empty section while a query was active bounced straight back here, which
    // (now that empty tabs are no longer disabled) made those tabs unvisitable —
    // you could click one but never land on it. It also makes the
    // non-looping argument structural rather than incidental: the effect does
    // not depend on the signal it writes.
    effect(() => {
      const query = this.filter.normalizedQuery();
      const items = this.railItems();
      if (query === "") {
        return;
      }
      untracked(() => {
        const active = items.find((item) => item.id === this.shell.active());
        if (!active || active.matchCount > 0) {
          return;
        }
        const firstWithMatches = items.find((item) => item.matchCount > 0);
        if (firstWithMatches) {
          this.shell.select(firstWithMatches.id);
        }
      });
    });

    // Refresh the OnPush-unsafe snapshot signals when the Diagnostics tab becomes
    // active. The old open/expand behavior triggered these refreshes; with
    // inspectors now always expanded when the tab is active, we refresh on tab
    // activation instead.
    effect(() => {
      if (this.shell.active() === "diagnostics") {
        this.refreshInspector("consents");
        this.refreshInspector("sessionStorage");
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  public refreshClicked(): void {
    this.errorsService.clearCriticalErrors();
    this.globalRunningJobsService.forceStop();
    this.loadingStateService.forceReset();
    this.dialogRef.close();
    this.router.navigate(["."]);
  }

  /**
   * Persist an offline-mode override and reload so the app re-bootstraps with the
   * new value. A reload is required because offlineMode is consumed at bootstrap
   * (auth guard, Apollo client, Keycloak init) and cannot change live.
   */
  setOfflineMode(enabled: boolean): void {
    if (enabled === this.offlineMode) {
      return;
    }
    setOfflineModeOverride(enabled, environment.production);
    this.dialogRef.close();
    globalThis.location.reload();
  }

  toggleAdvisorMode(): void {
    this.keycloakService.toggleForcedAdvisorMode();
  }

  generateDummyError(): void {
    Monitoring.error("Generating dummy error...");
    Log.error("Generated dummy error...");
  }

  stepUpAuth(): void {
    this.dialogRef.close();
    this.keycloakService.stepUpAuth();
  }

  logout(): void {
    this.dialogRef.close();
    this.keycloakService.logout({ redirectUri: "https://www.storebrand.no/logg-ut/" });
  }

  toggleDispatcherLogging(): void {
    this.dispatchLogging = this.dispatcherService.toggleLogging();
  }

  smartAccountStateSelect(value: string): void {
    if (!value) {
      return;
    }
    this.smartAccountService.overrideSmartAccount(this.SmartAccountState[value as SmartAccountState]);
  }

  extrapensionStateSelect(value: string): void {
    if (!value) {
      return;
    }
    this.extrapensionService.overrideExtrapension(
      Number(this.ExtrapensionState[value as keyof typeof ExtrapensionState]),
    );
  }

  ipsStateSelect(value: string): void {
    if (!value) {
      return;
    }
    this.actionIpsService.overrideIps(value as IpsAction);
  }

  setLogoutCookie(): void {
    this.hotjarService.setLogoutSurveyCookie();
  }

  public async copyToken(): Promise<void> {
    const token = await this.keycloakService.getToken();
    this.copyValueToClipboardService.copy(token ?? "", "Token copied!");
  }

  public async copyNin(): Promise<void> {
    const nin = await firstValueFrom(this.nin$);
    this.copyValueToClipboardService.copy(nin ?? "", "Nin copied!");
  }

  public async copyCMID(): Promise<void> {
    const cimid = await firstValueFrom(this.cmid$);
    this.copyValueToClipboardService.copy(cimid ?? "", "Customer id copied!");
  }

  public toggleFeatureOverride(key: string): void {
    this.featureToggleService.setOverride(key);
  }

  public navigateToPage(value: string): void {
    if (!value) {
      return;
    }
    // Read the value before close() destroys the view and detaches the <select>.
    const isPensionPlanRoute = value === pensionPlanRoutes[PlanRouteKey.Root];
    this.dialogRef.close();
    if (isPensionPlanRoute) {
      this.navigateToPensionPlanService.navigateToProgressUrl();
    } else {
      this.router.navigate([value], { queryParamsHandling: "merge" });
    }
  }

  public goToPensionPlanPage(value: string): void {
    if (!value) {
      return;
    }
    // Read the value before close() destroys the view and detaches the <select>.
    this.dialogRef.close();
    this.router.navigate([value], { queryParamsHandling: "merge" });
  }

  public removeContractsV2ModalCookie(): void {
    this.cookieService.deleteCookie(Cookies.RebrandingContractsModalShown);
  }

  public removeCampaignModalCookie(): void {
    this.cookieService.deleteCookie(Cookies.CampaignModalShown);
  }

  public copyBodyText(el: HTMLElement): void {
    this.copyValueToClipboardService.copy(el.textContent ?? "", "Copied!");
  }

  public onFilterInput(value: string): void {
    this.filter.setQuery(value);
  }

  public onFilterKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      // ModalBaseComponent (used by stb-modal) has its own
      // `@HostListener("document:keydown.escape")` that closes the modal; we
      // don't set `forceClickOnButtons`, so that listener is always active.
      // When the filter has text, Escape should clear it first —
      // stopPropagation() keeps the keypress from also bubbling up to
      // `document` and closing the whole panel. When the filter is already
      // empty there is nothing to clear, so we deliberately do nothing and
      // let the event bubble: Escape closes the modal, same as before.
      if (this.filter.query() !== "") {
        this.filter.clear();
        event.stopPropagation();
      }
      return;
    }

    if (event.key === "Enter") {
      const matches = this.rows().filter((row) => row.visible());
      if (matches.length === 1) {
        matches[0].activate();
      }
    }
  }

  public refreshInspector(id: InspectorId): void {
    if (id === "consents") {
      this.extractedConsents.set(this.consentService.extractAllConsents());
    } else if (id === "sessionStorage") {
      this.sessionStorageSnapshot.set(this.sessionStorageService.getAppData());
    }
  }

  public onAfpChange(value: string): void {
    if (value === "") {
      return;
    }
    this.profileService.setProfileHasAfp(value === "true");
  }

  public onPensionAgeChange(value: string): void {
    if (value === "") {
      return;
    }
    const age = Number(value);
    this.pensionPlanService.changeStartPayoutAge(age);
  }

  public onPresetChange(value: string): void {
    this.selectedPresetId.set(getPreset(value).preset);
  }

  /**
   * Persist the chosen scenario and reload. A reload is the same mechanism the offline-mode toggle
   * uses: httpResource only refetches when its URL signal changes or .reload() is called, and
   * dashboard.service.ts / settings.service.ts expose no reload at all.
   *
   * Also clears any AI mock chat override: `OfflineDataInterceptor` prefers it over the preset, so
   * leaving it in place would make this button silently do nothing.
   */
  public applyMockScenario(): void {
    clearAiMockOverride();
    writeMockScenario(MOCK_SCENARIO_PRESETS[this.selectedPresetId()]);
    this.dialogRef.close();
    globalThis.location.reload();
  }

  protected onRailKeydown(event: KeyboardEvent): void {
    // Every tab, including the ones the filter has emptied. Skipping empty tabs
    // meant that when the active tab was itself empty `currentIndex` was -1 and
    // the arrow keys silently did nothing.
    const items = this.railItems();

    const currentIndex = items.findIndex((item) => item.id === this.shell.active());
    if (currentIndex === -1) return;

    const keyToIndex: Record<string, number> = {
      ArrowDown: (currentIndex + 1) % items.length,
      ArrowUp: (currentIndex - 1 + items.length) % items.length,
      Home: 0,
      End: items.length - 1,
    };

    const nextIndex = keyToIndex[event.key];
    if (nextIndex === undefined) return;

    event.preventDefault();
    this.shell.select(items[nextIndex].id);
    // Focus the newly selected button
    const button = (event.target as HTMLElement)
      .closest('[role="tablist"]')
      ?.querySelector(`button[id="dev-tab-${items[nextIndex].id}"]`) as HTMLButtonElement | null;
    button?.focus();
  }
}

interface RailItem {
  readonly id: DevSectionId;
  readonly label: string;
  readonly icon: PhosphorIconName;
  /**
   * Rows in this section the filter leaves on screen. Comes from
   * `DevSectionDirective.matchingRowCount`, which deliberately does *not* call
   * `DevRowDirective.reachable` — see the comment there.
   */
  readonly matchCount: number;
  /**
   * Whether to print `matchCount` on the tab. False for "This page" (decision
   * D5-A).
   *
   * Note the original reason no longer holds: D5-A was chosen when "This page"
   * was to *mirror* rows from other sections, which would have made a tab count
   * that could not reconcile with the panel-wide `N / M`. The rows ended up
   * moved rather than mirrored, so the counts do reconcile now. The decision
   * stands as a display preference — "This page" also holds quicklinks, which
   * are not rows and so are not counted, making any number there misleading.
   */
  readonly showCount: boolean;
}

const RAIL_DEFS: readonly Omit<RailItem, "matchCount" | "showCount">[] = [
  { id: "page", label: "This page", icon: "compass" },
  { id: "settings", label: "Settings", icon: DEV_ROW_TYPE_ICONS.state },
  { id: "actions", label: "Actions", icon: DEV_ROW_TYPE_ICONS.action },
  { id: "goto", label: "Go to", icon: DEV_ROW_TYPE_ICONS.nav },
  { id: "diagnostics", label: "Diagnostics", icon: "stethoscope" },
];

interface PageLink {
  readonly label: string;
  readonly path: string;
  readonly current: boolean;
}

/**
 * The last non-empty segment of a page path, or `"/"` for the dashboard — the
 * sentinel `DevPanelContext.matchesRoute` uses for the segment-less root url.
 */
function lastRouteSegment(path: string): string {
  return (
    path
      .split("/")
      .filter((segment) => segment !== "")
      .pop() ?? "/"
  );
}

function getPages(): Map<string, string> {
  return new Map([
    ["Dashboard", `/${routes[RouteKey.Root]}`],
    ["Concept", routes[RouteKey.Concept]],
    ["PensionPlan", pensionPlanRoutes[PlanRouteKey.Root]],
    ["ContactUs", routes[RouteKey.ContactUs]],
    ["SmartAccount", smartAccountRoutes[SmartAccountRouteKey.Root]],
    ["Articles", articleRoutes[ArticlesRouteKey.Root]],
    ["Avtaler", contractsRoutes[ContractsRouteKey.Root]],
  ]);
}
